/**
 * dopamiles-stack-save.js
 * Tiered bundle promotion — radiogroup keyboard + click selection,
 * cartUpdate / variantChange subscribers, derived-string recompute
 * (footer summary + CTA + tier active/unlocked state). Pure DOM mutation.
 *
 * Eligibility heuristic: cartUpdate payload has no per-item product.tags
 * or collections (verified 2026-05-19), so the JS can't replicate the
 * server-side `bundle-eligible` collection predicate exactly. Instead it
 * counts every cart item EXCEPT the Shipping Protection variant (the only
 * permanent non-eligible line item), so SP never inflates the tier count.
 * SSR initial state uses the exact collection predicate; this keeps the
 * live recount aligned. Real enforcement is server-side in the Shopify
 * Function at checkout. This widget is a motivator, not a contract.
 *
 * Money: prefers window.Shopify.formatMoney + cart.money_format when
 * available; falls back to Intl.NumberFormat USD.
 *
 * Phone cases (data-offer="cases"): tiers carry buy / get / extra cents
 * instead of $ off each. A tile's price is buy × unit − extra, the footer
 * saving is get × unit + extra, and the CTA asks for buy − paid cases, where
 * paid = cases in cart − cases the best reached tier makes free. The count
 * comes from the drawer's [data-bnc-case-qty] marker; without it, /cart.js
 * lines are matched against the case product ids from the snippet. Cases never
 * use the count-every-line heuristic, which would count tees too.
 */

(function () {
  'use strict';

  var CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="m5 12 5 5L20 7"/></svg>';

  function debug() {
    if (window.bncDebug) console.log.apply(console, ['[bnc-stack]'].concat([].slice.call(arguments)));
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-bnc-stack]');
    if (!roots.length) return;
    roots.forEach(wireOne);
  }

  function wireOne(root) {
    if (root.__bncStackWired) return;
    root.__bncStackWired = true;

    var state = {
      picked: parseInt(root.dataset.defaultPicked, 10) || 3,
      unitCents: parseInt(root.dataset.unitCents, 10) || 0,
      inCart: parseInt(root.dataset.inCart, 10) || 0,
      maxQty: parseInt(root.dataset.maxQty, 10) || 5,
      collectionUrl: root.dataset.collectionUrl || '/collections/all',
      hasPickedSize: root.dataset.hasPickedSize === 'true',
      spVariantId: root.dataset.spVariantId || '',
      ctaText: root.dataset.ctaText || 'Shop {count} more tees →',
      isCases: root.dataset.offer === 'cases',
      countMarker: root.dataset.countMarker || 'data-bnc-eligible-qty',
      caseProductIds: (root.dataset.caseProductIds || '').split(',').filter(Boolean)
    };

    var tiers = collectTiers(root);
    if (!tiers.length) return;

    var els = {
      summaryQty: root.querySelector('[data-bnc-stack-summary-qty]'),
      summarySave: root.querySelector('[data-bnc-stack-summary-save]'),
      summary: root.querySelector('[data-bnc-stack-summary]'),
      footer: root.querySelector('.bnc-stack-foot')
    };

    bindRadioGroup(root, tiers, state, els);
    bindCartUpdate(state, tiers, els);
    bindVariantChange(root, state, tiers, els);
    if (state.isCases) bindCaseUnitPrice(state, tiers, els);

    render(state, tiers, els);
  }

  function collectTiers(root) {
    var btns = Array.prototype.slice.call(root.querySelectorAll('[role="radio"][data-tier-qty]'));
    return btns.map(function (btn) {
      var qty = parseInt(btn.dataset.tierQty, 10);
      return {
        qty: qty,
        off: parseInt(btn.dataset.tierOff, 10),
        // Case tiers only; undefined on tee tiers, which render() uses to pick the formula.
        buy: btn.dataset.tierBuy ? parseInt(btn.dataset.tierBuy, 10) : undefined,
        get: parseInt(btn.dataset.tierGet, 10) || 0,
        extraCents: parseInt(btn.dataset.tierExtraCents, 10) || 0,
        flag: btn.dataset.tierFlag || '',
        btn: btn,
        priceEl: btn.querySelector('[data-bnc-tier-price]')
      };
    });
  }

  // Effective per-unit price for a tier: unit price minus the tier's amount off,
  // clamped at zero. Null on bad input so render() can skip rather than print NaN.
  function effectiveUnitCents(unitCents, offDollars) {
    if (!isFinite(unitCents) || !isFinite(offDollars)) return null;
    return Math.max(0, unitCents - offDollars * 100);
  }

  // ── Money formatting ─────────────────────────────────────────────────────
  // Parses the shop's money_format, exposed as window.shopMoneyFormat from
  // layout/theme.liquid. Same algorithm as dopamiles-pdp-variant-sync.js, ported
  // locally rather than reusing its window.bncFmtMoney export: that script is
  // enqueued in theme.liquid while this one is emitted inside the section body,
  // so the export is not guaranteed to exist by the time the first render runs.
  //
  // The previous implementation fell back to Intl.NumberFormat with an undefined
  // locale, which renders in the SHOPPER's locale — a de-DE visitor saw the tier
  // price reformat to "27,99 $" on the first variant change while the SSR price
  // beside it stayed "$27.99". Server-supplied format only, no locale guessing.
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (thousands == null) thousands = ',';
    if (decimal == null) decimal = '.';
    if (isNaN(cents) || cents == null) return '0';
    var parts = (cents / 100).toFixed(precision).split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return dollars + (parts[1] ? decimal + parts[1] : '');
  }

  // `exact` keeps two decimals, matching Liquid's `money` filter. The tier price
  // needs it or a round price renders "$25" in SSR and "$25.00" after the first
  // reprice. The footer summary stays on the non-exact path ("Save $9").
  function formatMoney(cents, exact) {
    var format = window.shopMoneyFormat || '${{amount}}';
    var out;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { out = window.Shopify.formatMoney(cents, format); } catch (e) { out = null; }
    }

    if (out == null) {
      var placeholder = /\{\{\s*(\w+)\s*\}\}/;
      var match = format.match(placeholder);
      if (!match) return format;
      var value;
      switch (match[1]) {
        case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
        case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
        case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
        case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'"); break;
        default: value = formatWithDelimiters(cents, 2);
      }
      out = format.replace(placeholder, value);
    }

    return exact ? out : out.replace(/\.00$/, '');
  }

  function findTarget(tiers, picked) {
    for (var i = 0; i < tiers.length; i++) if (tiers[i].qty === picked) return tiers[i];
    return tiers[Math.floor(tiers.length / 2)];
  }

  // Cases the best reached tier makes free for `inCart` cases in the cart (same rule as the
  // native discounts and the cart headline): the highest tier whose buy + get fits.
  function freeCases(tiers, inCart) {
    var free = 0;
    tiers.forEach(function (t) { if (t.get && inCart >= t.qty) free = t.get; });
    return free;
  }

  function renderCases(state, tiers, els) {
    var target = findTarget(tiers, state.picked);
    var paid = state.inCart - freeCases(tiers, state.inCart);
    // Buy 1 has nothing to unlock, so it carries no CTA.
    var needed = target.get ? Math.max(0, target.buy - paid) : null;
    // Paid cases are in but the free one is not: Shopify frees nothing until it is
    // in the cart, so ask for it rather than claiming the tier is active.
    var freePending = needed === 0 ? Math.max(0, target.qty - state.inCart) : 0;

    tiers.forEach(function (t) {
      var active = t.qty === state.picked;
      t.btn.classList.toggle('active', active);
      t.btn.classList.toggle('unlocked', state.inCart >= t.qty && !active);
      t.btn.setAttribute('aria-checked', String(active));
      t.btn.setAttribute('tabindex', active ? '0' : '-1');
      syncTierBadge(t.btn, active, t.flag);
      if (t.priceEl) {
        t.priceEl.textContent = state.unitUnknown
          ? ''
          : formatMoney(Math.max(0, t.buy * state.unitCents - t.extraCents), true);
      }
    });

    if (els.summary) {
      els.summary.textContent = '';
      if (target.get) {
        var save = document.createElement('span');
        save.className = 'save';
        save.setAttribute('data-bnc-stack-summary-save', '');
        save.textContent = state.unitUnknown ? '' : 'Save ' + formatMoney(target.get * state.unitCents + target.extraCents);
        els.summary.appendChild(save);
      } else if (!state.unitUnknown) {
        els.summary.textContent = '1 case · ' + formatMoney(state.unitCents, true);
      }
    }

    var freeLabel = freePending === 1 ? 'Add your free case →' : 'Add your ' + freePending + ' free cases →';
    syncCta(els.footer, freePending || needed, state.collectionUrl, state.ctaText, true, freePending ? freeLabel : '');
  }

  function render(state, tiers, els) {
    if (state.isCases) return renderCases(state, tiers, els);
    var target = findTarget(tiers, state.picked);
    var needed = Math.max(0, target.qty - state.inCart);
    var savingsCents = target.qty * target.off * 100;

    tiers.forEach(function (t) {
      var active = t.qty === state.picked;
      var unlocked = state.inCart >= t.qty && !active;
      t.btn.classList.toggle('active', active);
      t.btn.classList.toggle('unlocked', unlocked);
      t.btn.setAttribute('aria-checked', String(active));
      t.btn.setAttribute('tabindex', active ? '0' : '-1');
      syncTierBadge(t.btn, active, t.flag);

      // Per-unit price. Absent when the tier table could not be trusted — the
      // snippet withholds the element rather than inventing a figure.
      if (t.priceEl) {
        if (state.unitUnknown) {
          // No variant resolves for the current selection, so product-info.js
          // has hidden the hero price row. Leaving the last variant's exact
          // price here would advertise a price for something unbuyable.
          t.priceEl.textContent = '';
        } else {
          var effCents = effectiveUnitCents(state.unitCents, t.off);
          if (effCents !== null) t.priceEl.textContent = formatMoney(effCents, true);
        }
      }
    });

    if (els.summaryQty) els.summaryQty.textContent = String(target.qty);
    if (els.summarySave) els.summarySave.textContent = 'Save ' + formatMoney(savingsCents);

    syncCta(els.footer, needed, state.collectionUrl, state.ctaText);
  }

  function syncTierBadge(btn, active, flagText) {
    var existingFlag = btn.querySelector('.bnc-stack-tier-flag');
    var existingCheck = btn.querySelector('.bnc-stack-tier-check');

    // Flag ("Most picked") persists whenever the tier carries flag text —
    // top-center, independent of selection. Check is the active-only marker.
    if (flagText && !existingFlag) {
      var flag = document.createElement('span');
      flag.className = 'bnc-stack-tier-flag';
      flag.textContent = flagText;
      btn.insertBefore(flag, btn.firstChild);
    } else if (!flagText && existingFlag) {
      existingFlag.remove();
    }

    if (active) {
      if (!existingCheck) {
        var check = document.createElement('span');
        check.className = 'bnc-stack-tier-check';
        check.setAttribute('aria-hidden', 'true');
        check.innerHTML = CHECK_SVG;
        btn.insertBefore(check, btn.firstChild);
      }
    } else if (existingCheck) {
      existingCheck.remove();
    }
  }

  // `needed` null hides the CTA (case Buy 1 tile). `singularize` turns "cases" into "case"
  // when one more is needed; the tee copy is left as written. `labelOverride` replaces the
  // "Shop N more" copy (the case "Add your free case" prompt).
  function syncCta(footer, needed, collectionUrl, ctaText, singularize, labelOverride) {
    if (!footer) return;
    var current = footer.querySelector('[data-bnc-stack-cta]');
    if (!current) return;
    var wasFocused = document.activeElement === current;

    current.hidden = needed === null;
    if (needed === null) return;

    if (needed === 0) {
      if (current.tagName !== 'BUTTON') {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cta';
        btn.setAttribute('data-bnc-stack-cta', '');
        btn.disabled = true;
        btn.setAttribute('aria-label', 'Bundle tier is active');
        btn.textContent = 'Tier active';
        current.replaceWith(btn);
      } else {
        current.disabled = true;
        current.setAttribute('aria-label', 'Bundle tier is active');
        current.textContent = 'Tier active';
      }
    } else {
      var label = (ctaText || 'Shop {count} more tees →').replace(/\{count\}/g, needed);
      if (singularize && needed === 1) label = label.replace(/\bcases\b/g, 'case');
      if (labelOverride) label = labelOverride;
      if (current.tagName !== 'A') {
        var a = document.createElement('a');
        a.className = 'cta';
        a.setAttribute('data-bnc-stack-cta', '');
        a.href = collectionUrl;
        a.textContent = label;
        current.replaceWith(a);
        if (wasFocused) a.focus();
      } else {
        current.href = collectionUrl;
        current.textContent = label;
      }
    }
  }

  function bindRadioGroup(root, tiers, state, els) {
    tiers.forEach(function (t, idx) {
      t.btn.addEventListener('click', function () {
        if (state.picked === t.qty) return;
        state.picked = t.qty;
        render(state, tiers, els);
        t.btn.focus();
      });

      t.btn.addEventListener('keydown', function (e) {
        var next = -1;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (idx + 1) % tiers.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (idx - 1 + tiers.length) % tiers.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = tiers.length - 1;
            break;
          case ' ':
          case 'Enter':
            e.preventDefault();
            if (state.picked !== t.qty) {
              state.picked = t.qty;
              render(state, tiers, els);
            }
            return;
          default:
            return;
        }
        e.preventDefault();
        state.picked = tiers[next].qty;
        render(state, tiers, els);
        tiers[next].btn.focus();
      });
    });
  }

  function bindCartUpdate(state, tiers, els) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      debug('subscribe/PUB_SUB_EVENTS unavailable — cart updates skipped');
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      // Prefer the server-authoritative bundle-eligible count emitted by the
      // cart drawer (exact `bundle-eligible` collection predicate — mirrors the
      // checkout Function). The cart-line payloads carry no per-item
      // collections, so the JS heuristic below counts every non-SP line and
      // over-states the tier when non-eligible items (caps, sticker packs,
      // gift cards) are in cart. The drawer content is swapped before this
      // event fires, so the marker is fresh.
      var serverCount = readEligibleQty(state.countMarker);
      if (serverCount !== null) {
        state.inCart = serverCount;
        render(state, tiers, els);
        return;
      }
      if (state.isCases) {
        fetchCart().then(function (cart) {
          var c = caseCount(cart, state.caseProductIds);
          if (typeof c !== 'number') return;
          state.inCart = c;
          render(state, tiers, els);
        });
        return;
      }
      // Fallback (drawer absent): SP-only exclusion heuristic.
      var cartData = data && data.cartData;
      var count = eligibleCount(cartData, state.spVariantId);
      if (typeof count === 'number') {
        state.inCart = count;
        render(state, tiers, els);
        return;
      }
      fetchCart().then(function (cart) {
        var c = eligibleCount(cart, state.spVariantId);
        if (typeof c !== 'number') return;
        state.inCart = c;
        render(state, tiers, els);
      });
    });
  }

  // Read the exact count the cart drawer computed server-side: tees use
  // data-bnc-eligible-qty, cases data-bnc-case-qty. Returns null when the
  // marker is absent (drawer not on page yet).
  function readEligibleQty(attr) {
    var el = document.querySelector('[' + attr + ']');
    if (!el) return null;
    var raw = el.getAttribute(attr);
    if (raw === null || raw === '') return null;
    var n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  // Sum line-item quantities, excluding the Shipping Protection variant.
  // Returns null when the payload carries no usable items array.
  function eligibleCount(cart, spVariantId) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var sp = spVariantId ? String(spVariantId) : '';
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (sp && String(item.variant_id) === sp) continue;
      total += typeof item.quantity === 'number' ? item.quantity : 0;
    }
    return total;
  }

  // Sum quantities of lines whose product is in the case collection.
  function caseCount(cart, productIds) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var total = 0;
    cart.items.forEach(function (item) {
      if (productIds.indexOf(String(item.product_id)) >= 0) total += item.quantity || 0;
    });
    return total;
  }

  // case-picker.js reports the chosen case type's price before a model is picked, when no
  // variantChange fires, so MagSafe tiles reprice as soon as MagSafe is tapped.
  function bindCaseUnitPrice(state, tiers, els) {
    document.addEventListener('bnc:case-unit-price', function (e) {
      var cents = e.detail && e.detail.cents;
      if (typeof cents !== 'number' || !cents) return;
      state.unitUnknown = false;
      state.unitCents = cents;
      render(state, tiers, els);
    });
  }

  function fetchCart() {
    if (typeof fetch !== 'function') return Promise.resolve(null);
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return fetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function bindVariantChange(root, state, tiers, els) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
      var variant = payload && payload.data && payload.data.variant;
      if (!variant || typeof variant.price !== 'number') {
        // product-info.js publishes no variant when the chosen combination is
        // unavailable, and hides the hero price row. Blank the tier prices to
        // match instead of leaving the previous variant's figures on screen.
        state.unitUnknown = true;
        render(state, tiers, els);
        return;
      }
      state.unitUnknown = false;
      state.unitCents = variant.price;
      state.hasPickedSize = true;
      root.dataset.hasPickedSize = 'true';
      render(state, tiers, els);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // The theme editor re-renders the whole section on every block setting change,
  // including the Stack & Save spot dropdown, which replaces the .bnc-stack node.
  // The replacement carries no __bncStackWired marker and no listeners, so
  // without this the merchant's first use of that dropdown leaves a card whose
  // tiers no longer respond to clicks or arrow keys — silently, with no console
  // error. Same pattern as dopamiles-pdp-variant-sync.js and dopamiles-header.js.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
